import React, { useState, useEffect } from 'react';
import { StyleSheet, ActivityIndicator } from 'react-native';
import SlideModal from '@/Atoms/SlideModal';
import Box from '@/Atoms/Box';
import CCText from '@/Atoms/CCText';
import CCButton from '@/Atoms/CCButton';
import CCColors from '@/Utils/CCColors';
import { FontSizes } from '@/Utils/Dimensions';
import { formatCurrency, getUserDetails } from '@/Utils/CommonUtils';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import dayjs from 'dayjs';
import useCreateRenewalOrder from '@/Hooks/RenewalApi/useCreateRenewalOrder';
import { SelectedSlot } from '@/Hooks/RenewalApi/useCreateRenewalOrder';
import useGetProfileApi from '@/Hooks/ProfileApi/profileApi';
import useSendRenewalPaymentInitiationWhatsapp from '@/Hooks/RenewalApi/useSendRenewalPaymentInitiationWhatsapp';

interface PaymentConfirmationModalProps {
  visible: boolean;
  onClose: () => void;
  onPaymentSuccess?: (orderId: string) => void;
  renewalData: any;
  selectedPlanType: 'personal' | 'group' | 'webinar';
  selectedDuration: number;
  selectedSlots: SelectedSlot[];
  selectedCoach?: 'general' | 'international_master' | 'grand_master';
  selectedBatch?: {
    batch_name: string;
    batch_code: string;
    coach_name: string;
    class_timing: string;
    class_days: string;
    start_date: string;
    tournament_id: number;
    level_name: string | null;
  } | null;
  pricingBreakdown: {
    basePrice: number;
    maxBasePrice?: number;
    offerPrice?: number;
    offerDiscountAmount?: number;
    offerDiscountPercentage?: number;
    durationDiscount: number;
    durationDiscountPct: number;
    couponDiscount: number;
    gst: number;
    grandTotal: number;
    monthlyEquivalent: number;
    currency: string;
  } | null;
  appliedCoupon?: any;
  selectedPricing?: {
    level_name?: string | null;
    level_slug?: string | null;
    format_name?: string | null;
    format_slug?: string | null;
  } | null;
  selectedPricingId?: number; // Pricing ID from selected pricing entry
}

const PaymentConfirmationModal: React.FC<PaymentConfirmationModalProps> = ({
  visible,
  onClose,
  onPaymentSuccess,
  renewalData,
  selectedPlanType,
  selectedDuration,
  selectedSlots,
  selectedCoach = 'general',
  selectedBatch = null,
  pricingBreakdown,
  appliedCoupon,
  selectedPricing,
  selectedPricingId,
}) => {
  const getCoachLabel = (coach: 'general' | 'international_master' | 'grand_master'): string => {
    switch (coach) {
      case 'grand_master':
        return 'Grand Masters';
      case 'international_master':
        return 'International Master';
      default:
        return 'Fide Rated Coach';
    }
  };
  const [userEmail, setUserEmail] = useState<string>('');

  const getPlanTypeLabel = () => {
    switch (selectedPlanType) {
      case 'personal':
        return 'Personal 1:1';
      case 'group':
        return 'Small Group';
      case 'webinar':
        return 'Masterclass';
      default:
        return 'Plan';
    }
  };

  // Fetch profile to get email
  const { mutate: getProfile } = useGetProfileApi(
    (data) => {
      if (data?.success && data?.data?.user?.[0]?.fields?.email) {
        setUserEmail(data.data.user[0].fields.email);
      }
    },
    (error) => {
      console.error('Error fetching profile:', error);
    }
  );

  // Fetch profile when modal opens
  useEffect(() => {
    if (visible) {
      getProfile();
    }
  }, [visible]);

  const { mutate: sendPaymentInitiationWhatsapp } = useSendRenewalPaymentInitiationWhatsapp(
    () => {
      // WhatsApp sent successfully, continue with payment
    },
    (error) => {
      // Log error but don't block payment flow
      console.error('Error sending payment initiation WhatsApp:', error);
    },
  );

  const { mutate: createRenewalOrder, isLoading: isCreatingOrder } = useCreateRenewalOrder(
    (data) => {
      // Handle successful payment link generation
      const orderId = data?.data?.order_id || data?.order_id || `CSOC-${dayjs().format('YYYY-MM-DD')}-${Math.floor(Math.random() * 1000000)}`;
      
      // Get payment URL
      const paymentLink = data?.data?.payment_link || data?.content?.payment_links?.web;
      
      if (paymentLink) {
        // Close confirmation modal and navigate to payment URL on same page
        onClose();
        if (typeof window !== 'undefined') {
          window.location.href = paymentLink;
        }
      } else {
        // Fallback: Close confirmation modal and show success modal immediately
        onClose();
        if (onPaymentSuccess) {
          onPaymentSuccess(orderId);
        }
      }
    },
    (error) => {
      console.error('Error creating renewal order:', error);
      // You might want to show an error message here
    },
  );

  const handlePay = async () => {
    if (!renewalData || !pricingBreakdown) return;

    // Send payment initiation WhatsApp before creating order
    sendPaymentInitiationWhatsapp({
      plan_type: selectedPlanType,
      duration_months: selectedDuration,
      amount: pricingBreakdown.grandTotal,
      currency: pricingBreakdown?.currency || 'INR',
    });

    // For testing: Skip API call and show success modal immediately
    // const orderId = `CSOC-${dayjs().format('YYYY-MM-DD')}-${Math.floor(Math.random() * 1000000)}`;
    // onClose();
    // if (onPaymentSuccess) {
    //   onPaymentSuccess(orderId);
    // }

    // Uncomment below to enable actual API call in production
    
    const user = renewalData.user;
    // Construct return URL pointing to SuccessPage
    // The payment gateway will append order_id as a query parameter when redirecting
    const returnUrl = typeof window !== 'undefined' 
      ? `${window.location.origin}/SuccessPage`
      : 'https://learn.circlechess.com/SuccessPage';

    // Use email from profile API, fallback to user.email or mobile-based email
    const email = userEmail || user.email || `${user.mobile}@circlechess.com`;
    let userDetails = await getUserDetails();
    userDetails = JSON.parse(userDetails);
    const player_id = userDetails?.player;
    // Get selectedPricing from renewalData using selectedPricingId
    const selectedPricingEntry = selectedPricingId 
      ? renewalData?.pricing?.find((p: any) => p.id === selectedPricingId)
      : null;
    
    createRenewalOrder({
      tournament_id: selectedPricingEntry?.tournament_id || 
                     renewalData.recommended_next_level?.tournament_id || 
                     renewalData.current_subscription.tournament_id,
      plan_type: selectedPlanType,
      duration_months: selectedDuration,
      coupon_code: appliedCoupon?.code,
      selectedSlots: selectedSlots,
      selectedBatch: selectedBatch,
      customer_id: user.id,
      player_id: player_id,
      mobile_number: user.mobile,
      email: email,
      return_url: returnUrl,
      timezone: 'Asia/Kolkata',
      amount: pricingBreakdown.grandTotal, // Send calculated grand total
      selected_pricing_id: selectedPricingId, // Pass selected pricing ID
      category_str: `${pricingBreakdown?.currency || 'INR'} /${selectedDuration}-Months`, // CSOC payment category string (format: "INR /3-Months" for backend API)
    });
    
  };

  return (
    <SlideModal
      visible={visible}
      onClose={onClose}
      type="center"
      modalHeaderTitle={
        <Box flexDirection="row" alignItems="center" gap={8}>
          <MaterialCommunityIcons name="check-circle" size={20} color={CCColors.Primary.Default} />
          <CCText fontSize={FontSizes[18]} fontWeight="bold">
            Confirm Your Selection
          </CCText>
        </Box>
      }
      width={0.8}
      minWidth={350}
      showCloseIcon={true}>
      <Box padding={20}>
        {!pricingBreakdown ? (
          <Box alignItems="center" paddingVertical={40}>
            <CCText fontSize={FontSizes[16]} color={CCColors.TextColor.Dark} marginBottom={10}>
              Unable to load pricing information
            </CCText>
            <CCText fontSize={FontSizes[14]} color={CCColors.TextColor.Grey} textAlign="center">
              Please go back and select a plan to continue
            </CCText>
            <CCButton
              onPress={onClose}
              style={[styles.button, styles.cancelButton, { marginTop: 20 }]}
              contentContainerStyle={styles.buttonContent}>
              <CCText fontSize={FontSizes[14]} fontWeight="bold" color={CCColors.TextColor.Dark}>
                Go Back
              </CCText>
            </CCButton>
          </Box>
        ) : (
          <>
        {/* Your Selection */}
        <Box marginBottom={20}>
          <CCText fontSize={FontSizes[16]} fontWeight="bold" color={CCColors.TextColor.Dark} marginBottom={15}>
            Your Selection
          </CCText>

          {/* Level */}
          {selectedPricing?.level_name && (
            <Box flexDirection="row" justifyContent="space-between" marginBottom={12}>
              <CCText fontSize={FontSizes[14]} color={CCColors.TextColor.Grey}>
                Level
              </CCText>
              <CCText fontSize={FontSizes[14]} fontWeight="semibold" color={CCColors.TextColor.Dark}>
                {selectedPricing.level_name}
              </CCText>
            </Box>
          )}

          {/* Plan Type */}
          <Box flexDirection="row" justifyContent="space-between" marginBottom={12}>
            <CCText fontSize={FontSizes[14]} color={CCColors.TextColor.Grey}>
              Plan Type
            </CCText>
            <CCText fontSize={FontSizes[14]} fontWeight="semibold" color={CCColors.TextColor.Dark}>
              {getPlanTypeLabel()}
            </CCText>
          </Box>

          {/* Duration */}
          <Box flexDirection="row" justifyContent="space-between" marginBottom={12}>
            <CCText fontSize={FontSizes[14]} color={CCColors.TextColor.Grey}>
              Duration
            </CCText>
            <CCText fontSize={FontSizes[14]} fontWeight="semibold" color={CCColors.TextColor.Dark}>
              {selectedDuration} Months
            </CCText>
          </Box>

          {/* Coach Selection */}
          <Box flexDirection="row" justifyContent="space-between" marginBottom={12}>
            <CCText fontSize={FontSizes[14]} color={CCColors.TextColor.Grey}>
              Coach
            </CCText>
            <CCText fontSize={FontSizes[14]} fontWeight="semibold" color={CCColors.TextColor.Dark}>
              {getCoachLabel(selectedCoach)}
            </CCText>
          </Box>

          {/* Selected Batch (for group plan type) */}
          {selectedPlanType === 'group' && selectedBatch && (
            <Box marginBottom={12}>
              <CCText fontSize={FontSizes[14]} color={CCColors.TextColor.Grey} marginBottom={8}>
                Selected Batch
              </CCText>
              <Box
                padding={14}
                backgroundColor={CCColors.Primary.lightBrown}
                borderRadius={10}
                borderWidth={2}
                borderColor={CCColors.Primary.Default}>
                {/* Compact 2-line display: Line 1 - Batch Name + Coach */}
                <Box flexDirection="row" flexWrap="wrap" alignItems="center" gap={8} marginBottom={6}>
                  <CCText
                    fontSize={FontSizes[13]}
                    fontWeight="semibold"
                    color={CCColors.TextColor.Dark}
                    numberOfLines={1}
                    style={{flex: 1, minWidth: 120}}>
                    {selectedBatch.batch_name}
                  </CCText>
                  {selectedBatch.coach_name && (
                    <Box flexDirection="row" alignItems="center" gap={4}>
                      <MaterialCommunityIcons name="account" size={12} color={CCColors.TextColor.Grey} />
                      <CCText fontSize={FontSizes[11]} color={CCColors.TextColor.Grey} numberOfLines={1}>
                        {selectedBatch.coach_name}
                      </CCText>
                    </Box>
                  )}
                </Box>
                {/* Line 2 - Days + Time */}
                <Box flexDirection="row" flexWrap="wrap" alignItems="center" gap={8}>
                  {selectedBatch.class_days && (
                    <Box flexDirection="row" alignItems="center" gap={4}>
                      <MaterialCommunityIcons name="calendar-week" size={12} color={CCColors.TextColor.Grey} />
                      <CCText fontSize={FontSizes[11]} color={CCColors.TextColor.Grey} numberOfLines={1}>
                        {selectedBatch.class_days.split(',').map(d => {
                          const dayMap: {[key: string]: string} = {
                            '1': 'Mon', '2': 'Tue', '3': 'Wed',
                            '4': 'Thu', '5': 'Fri', '6': 'Sat', '7': 'Sun'
                          };
                          return dayMap[d.trim()] || d.trim();
                        }).join(', ')}
                      </CCText>
                    </Box>
                  )}
                  {selectedBatch.class_timing && (
                    <Box flexDirection="row" alignItems="center" gap={4}>
                      <MaterialCommunityIcons name="clock-outline" size={12} color={CCColors.TextColor.Grey} />
                      <CCText fontSize={FontSizes[11]} color={CCColors.TextColor.Grey} numberOfLines={1}>
                        {selectedBatch.class_timing.split(',').slice(0, 2).join(', ')}
                        {selectedBatch.class_timing.split(',').length > 2 ? '...' : ''}
                      </CCText>
                    </Box>
                  )}
                  {selectedBatch.start_date && (
                    <Box flexDirection="row" alignItems="center" gap={8}>
                      <MaterialCommunityIcons
                        name="calendar-start"
                        size={16}
                        color={CCColors.TextColor.Dark}
                      />
                      <CCText fontSize={FontSizes[12]} color={CCColors.TextColor.Dark} fontWeight="semibold">
                        Starts:
                      </CCText>
                      <CCText fontSize={FontSizes[12]} color={CCColors.TextColor.Dark}>
                        {dayjs(selectedBatch.start_date).format('MMM D, YYYY')}
                      </CCText>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          )}

          {/* Custom Slots for Group Plan Type */}
          {selectedPlanType === 'group' && selectedSlots.length > 0 && (
            <Box marginBottom={12}>
              <CCText fontSize={FontSizes[14]} color={CCColors.TextColor.Grey} marginBottom={8}>
                Custom Time Slots ({selectedSlots.length})
              </CCText>
              <Box
                padding={14}
                backgroundColor={CCColors.Grey.Divider200}
                borderRadius={10}
                borderWidth={1}
                borderColor={CCColors.BorderBottom}>
                <Box flexDirection="row" flexWrap="wrap" gap={6} marginBottom={8}>
                  {selectedSlots.map((slot, index) => {
                    const slotDate = dayjs(slot.date);
                    const dayName = slotDate.format('ddd');
                    const dateStr = slotDate.format('MMM D');
                    return (
                      <Box
                        key={`${slot.date}_${slot.time}_${index}`}
                        flexDirection="row"
                        alignItems="center"
                        gap={6}
                        backgroundColor={CCColors.White}
                        paddingHorizontal={10}
                        paddingVertical={6}
                        borderRadius={6}
                        borderWidth={1}
                        borderColor={CCColors.BorderBottom}>
                        <MaterialCommunityIcons
                          name="calendar-clock"
                          size={14}
                          color={CCColors.Primary.Default}
                        />
                        <CCText
                          fontSize={FontSizes[11]}
                          color={CCColors.TextColor.Dark}
                          fontWeight="semibold">
                          {dayName} {dateStr}
                        </CCText>
                        <CCText
                          fontSize={FontSizes[11]}
                          color={CCColors.TextColor.Dark}>
                          {slot.time.includes('T')
                            ? dayjs(slot.time).format('h:mm A')
                            : dayjs(`2000-01-01 ${slot.time}`).format('h:mm A')}
                        </CCText>
                      </Box>
                    );
                  })}
                </Box>
                <Box padding={8} backgroundColor={CCColors.Primary.lightYellow} borderRadius={6}>
                  <CCText fontSize={FontSizes[11]} color={CCColors.TextColor.Dark} lineHeight={16}>
                    <MaterialCommunityIcons name="information" size={12} color={CCColors.Primary.Dark} />{' '}
                    Our team will contact you to find the best batch matching your preferences.
                  </CCText>
                </Box>
              </Box>
            </Box>
          )}

          {/* Selected Slots (for personal/webinar plan types) */}
          {selectedSlots.length > 0 && selectedPlanType !== 'group' && (
            <Box marginBottom={12}>
              <CCText fontSize={FontSizes[14]} color={CCColors.TextColor.Grey} marginBottom={8}>
                Selected Time Slots ({selectedSlots.length})
              </CCText>
              <Box flexDirection="row" flexWrap="wrap" gap={6}>
                {selectedSlots.map((slot, index) => {
                  const slotDate = dayjs(slot.date);
                  const dayName = slotDate.format('ddd');
                  const timeStr = slot.time.includes('T')
                    ? dayjs(slot.time).format('h:mm A')
                    : dayjs(`2000-01-01 ${slot.time}`).format('h:mm A');
                  return (
                    <Box
                      key={`${slot.date}_${slot.time}_${index}`}
                      paddingHorizontal={10}
                      paddingVertical={6}
                      backgroundColor={CCColors.Primary.lightBrown}
                      borderRadius={6}
                      borderWidth={1}
                      borderColor={CCColors.Primary.Default}>
                      <CCText fontSize={FontSizes[12]} color={CCColors.TextColor.Dark} fontWeight="semibold">
                        {dayName} {timeStr}
                      </CCText>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          {/* Applied Coupon */}
          {appliedCoupon && (
            <Box flexDirection="row" justifyContent="space-between" marginBottom={12}>
              <CCText fontSize={FontSizes[14]} color={CCColors.TextColor.Grey}>
                Coupon Applied
              </CCText>
              <Box alignItems="flex-end">
                <CCText fontSize={FontSizes[14]} fontWeight="semibold" color={CCColors.Green}>
                  {appliedCoupon.code}
                </CCText>
                <CCText fontSize={FontSizes[12]} color={CCColors.Green}>
                  {appliedCoupon.discount_type === 'percentage'
                    ? `${appliedCoupon.discount_value}% OFF`
                    : `${formatCurrency(parseFloat(appliedCoupon.discount_value), pricingBreakdown.currency)} OFF`}
                </CCText>
              </Box>
            </Box>
          )}
        </Box>

        {/* Amount Breakdown */}
        <Box marginBottom={20}>
          <CCText fontSize={FontSizes[16]} fontWeight="bold" color={CCColors.TextColor.Dark} marginBottom={15}>
            Amount Breakdown
          </CCText>

          {/* Base Fee (GST Inclusive) */}
          <Box flexDirection="row" justifyContent="space-between" alignItems="center" marginBottom={10}>
            <CCText fontSize={FontSizes[14]} color={CCColors.TextColor.Dark}>
              Base Fee{pricingBreakdown.currency === 'INR' ? ' (incl. GST)' : ''}
            </CCText>
            <Box alignItems="flex-end">
              <Box flexDirection="row" alignItems="center" gap={8}>
                <CCText
                  fontSize={FontSizes[14]}
                  fontWeight="semibold"
                  color={CCColors.TextColor.Dark}>
                  {formatCurrency(pricingBreakdown.maxBasePrice || pricingBreakdown.basePrice, pricingBreakdown.currency)}
                </CCText>
                {pricingBreakdown.offerDiscountPercentage && pricingBreakdown.offerDiscountPercentage > 0 ? (
                  <Box
                    backgroundColor={CCColors.Orange}
                    paddingHorizontal={6}
                    paddingVertical={2}
                    borderRadius={4}>
                    <CCText
                      fontSize={FontSizes[10]}
                      color={CCColors.White}
                      fontWeight="bold">
                      OFFER
                    </CCText>
                  </Box>
                ):<></>}
              </Box>
            </Box>
          </Box>

          {/* Early Renewal Discount */}
          {pricingBreakdown.durationDiscount > 0 && (
            <Box flexDirection="row" justifyContent="space-between" marginBottom={10}>
              <CCText fontSize={FontSizes[14]} color={CCColors.TextColor.Dark}>
                Duration Discount{pricingBreakdown.durationDiscountPct > 0 ? ` (${pricingBreakdown.durationDiscountPct}% off)` : ''}
              </CCText>
              <CCText fontSize={FontSizes[14]} fontWeight="semibold" color={CCColors.Green}>
                -{formatCurrency(pricingBreakdown.durationDiscount, pricingBreakdown.currency)}
              </CCText>
            </Box>
          )}

          {/* Coupon Discount */}
          {pricingBreakdown.couponDiscount > 0 && (
            <Box flexDirection="row" justifyContent="space-between" marginBottom={10}>
              <CCText fontSize={FontSizes[14]} color={CCColors.TextColor.Dark}>
                Coupon Discount
              </CCText>
              <CCText fontSize={FontSizes[14]} fontWeight="semibold" color={CCColors.Green}>
                -{formatCurrency(pricingBreakdown.couponDiscount, pricingBreakdown.currency)}
              </CCText>
            </Box>
          )}

          {/* GST Breakdown (for information) - Only show for INR */}
          {pricingBreakdown.currency === 'INR' && (
            <Box flexDirection="row" justifyContent="space-between" marginBottom={10}>
              <CCText fontSize={FontSizes[14]} color={CCColors.TextColor.Dark}>
                GST (18%) <CCText fontSize={FontSizes[12]} color={CCColors.TextColor.Grey}>(included)</CCText>
              </CCText>
              <CCText fontSize={FontSizes[14]} fontWeight="semibold" color={CCColors.TextColor.Dark}>
                {formatCurrency(pricingBreakdown.gst, pricingBreakdown.currency)}
              </CCText>
            </Box>
          )}

          {/* Divider */}
          <Box height={1} backgroundColor={CCColors.Grey.Divider200} marginBottom={10} />

          {/* Grand Total */}
          <Box flexDirection="row" justifyContent="space-between" alignItems="center">
            <CCText fontSize={FontSizes[16]} fontWeight="bold" color={CCColors.TextColor.Dark}>
              Grand Total
            </CCText>
            <Box alignItems="flex-end">
              <CCText fontSize={FontSizes[18]} fontWeight="bold" color={CCColors.Primary.Default}>
                {formatCurrency(pricingBreakdown.grandTotal, pricingBreakdown.currency)}
              </CCText>
              <CCText fontSize={FontSizes[12]} color={CCColors.TextColor.Grey}>
                ({formatCurrency(pricingBreakdown.monthlyEquivalent, pricingBreakdown.currency)} per month)
              </CCText>
            </Box>
          </Box>
        </Box>

        {/* Timing Notice */}
        {selectedSlots.length > 0 && (
          <Box
            flexDirection="row"
            alignItems="flex-start"
            gap={10}
            padding={15}
            backgroundColor={CCColors.Orange + '15'}
            borderRadius={8}
            borderWidth={1}
            borderColor={CCColors.Orange}
            marginBottom={20}>
            <MaterialCommunityIcons name="information" size={20} color={CCColors.Orange} />
            <Box flex={1}>
              <CCText fontSize={FontSizes[13]} color={CCColors.TextColor.Dark} fontWeight="bold" marginBottom={4}>
                Important Notice
              </CCText>
              <CCText fontSize={FontSizes[12]} color={CCColors.TextColor.Dark} lineHeight={18}>
                The selected time slots might vary and will not be the same after confirming with you. We will reach out to finalize the schedule.
              </CCText>
            </Box>
          </Box>
        )}

        {/* Action Buttons */}
        <Box flexDirection="row" gap={12}>
          <CCButton
            onPress={onClose}
            style={[styles.button, styles.cancelButton]}
            contentContainerStyle={styles.buttonContent}>
            <CCText fontSize={FontSizes[14]} fontWeight="bold" color={CCColors.TextColor.Dark}>
              Cancel
            </CCText>
          </CCButton>
          <CCButton
            onPress={handlePay}
            disabled={isCreatingOrder || !pricingBreakdown || pricingBreakdown.grandTotal <= 0}
            style={[styles.button, styles.payButton]}
            contentContainerStyle={styles.buttonContent}>
            {isCreatingOrder ? (
              <Box flexDirection="row" alignItems="center" gap={8}>
                <ActivityIndicator size="small" color={CCColors.White} />
                <CCText fontSize={FontSizes[14]} fontWeight="bold" color={CCColors.White}>
                  Processing...
                </CCText>
              </Box>
            ) : (
              <Box flexDirection="row" alignItems="center" gap={8}>
                <MaterialCommunityIcons name="lock" size={18} color={CCColors.White} />
                <CCText fontSize={FontSizes[14]} fontWeight="bold" color={CCColors.White}>
                  Pay {formatCurrency(pricingBreakdown.grandTotal, pricingBreakdown.currency)}
                </CCText>
              </Box>
            )}
          </CCButton>
        </Box>
        </>
        )}
      </Box>
    </SlideModal>
  );
};

const styles = StyleSheet.create({
  button: {
    flex: 1,
    borderRadius: 8,
    paddingVertical: 14,
  },
  cancelButton: {
    backgroundColor: CCColors.Grey.Divider200,
  },
  payButton: {
    backgroundColor: CCColors.Primary.Default,
  },
  buttonContent: {
    padding: 0,
  },
});

export default PaymentConfirmationModal;

